import { star } from "@/figmaElements";
import { useFootLabel } from "@/hooks/useFootLabel";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { bodyTypeIdToName } from "@/redux/squadBuilder/squadBuilderUtils";
import { useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

interface PlayerMainStatsSectionProps {
  positions: string[];
  weakFoot: number;
  skillMoves: number;
  preferredFoot: string;
  bodyTypeId: number;
}

const Tag = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-panel-background px-2 py-1 mobile:rounded-3xl text-xs font-semibold text-dark-secondary">
      {children}
    </div>
  );
};

const PlayerMainStatsSection = ({
  positions,
  weakFoot,
  skillMoves,
  preferredFoot,
  bodyTypeId,
}: PlayerMainStatsSectionProps) => {
  const { positions: positionLabels } = useSelector(contentLabelsSelector);
  const footLabel = useFootLabel();
  const { t } = useTranslation("players");
  return (
    <div className="mobile:min-h-16 col-span-4 mobile:col-span-1">
      <div className="flex flex-row mobile:hidden gap-0.5 h-10 text-tiny items-center font-semibold">
        <div className="bg-panel-background flex flex-wrap rounded-tl-md items-center h-full w-full justify-center gap-1">
          {positions?.map((position) => (
            <span key={position}>
              {positionLabels[position]?.name ?? position}
            </span>
          ))}
        </div>
        <div className="flex flex-row gap-2 bg-panel-background h-full w-full items-center justify-center min-w-24">
          {skillMoves && (
            <div className="flex flex-row gap-0.5 items-center">
              {t("detailed.mainStats.skillMovesShort", { value: skillMoves })}
              <div className="w-3 h-3">{star}</div>
            </div>
          )}
          {weakFoot && (
            <div className="flex flex-row gap-0.5 items-center">
              {t("detailed.mainStats.weakFootShort", { value: weakFoot })}
              <div className="w-3 h-3">{star}</div>
            </div>
          )}
        </div>
        <div className="h-full bg-panel-background flex items-center justify-center w-full">
          {preferredFoot && (
            <span>
              {t("detailed.mainStats.foot", { foot: footLabel(preferredFoot) })}
            </span>
          )}
        </div>
        <div className="h-full bg-panel-background flex items-center justify-center rounded-tr-md w-full">
          {bodyTypeId && <span>{bodyTypeIdToName[bodyTypeId]?.[0]}</span>}
        </div>
      </div>
      <div className="hidden mobile:flex flex-wrap gap-2">
        {positions?.map((position) => (
          <Tag key={position}>{positionLabels[position]?.name ?? position}</Tag>
        ))}
        {skillMoves && (
          <Tag>
            <div className="flex flex-row gap-0.5 items-center">
              {t("detailed.mainStats.skillMoves", { value: skillMoves })}
              <div className="h-3 w-3">{star}</div>
            </div>
          </Tag>
        )}
        {weakFoot && (
          <Tag>
            <div className="flex flex-row gap-0.5 items-center">
              {t("detailed.mainStats.weakFoot", { value: weakFoot })}
              <div className="w-3 h-3">{star}</div>
            </div>
          </Tag>
        )}
        {preferredFoot && (
          <Tag>
            {t("detailed.mainStats.foot", { foot: footLabel(preferredFoot) })}
          </Tag>
        )}
        {bodyTypeId && <Tag>{bodyTypeIdToName[bodyTypeId]?.[0]}</Tag>}
      </div>
    </div>
  );
};

export default PlayerMainStatsSection;
